import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { GlobalContext } from "../../components/context/GlobalContext";
import MyTeams from "./teams/MyTeams";
import TeamApplicationsSent from "./invitations/TeamApplicationsSent";
import InvitesReceived from "./invitations/InvitesReceived";
import { AppBar, Box, Tab, Tabs, Badge } from "@mui/material/";
import { makeStyles } from '@mui/styles';
import useScreenSize from "../customHooks/useScreenSize";
import TeamDashboardMobile from "./TeamDashboardMobile";
import api from "components/settings/api";

const useStyles = makeStyles((theme) => ({
  containerWrapper: {
    flexGrow: 1,
    maxWidth: "760px",
    maxHeight: "100%",
    marginTop: "40px",
    padding: "30px",
    marginBottom: "20px",
    marginLeft: 0,
    backgroundColor: "#FFFFFF",
    boxShadow: "0px 12px 26px -34px #283136",
    border: "1px solid rgba(0, 27, 17, 0.1)",
    borderRadius: "4px",
    [theme.breakpoints.down('lg')]: {
      maxWidth: "100%",
      padding: "5px",
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: "84.3vw",
    },
  },
  tabs: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    width: "100%",
    [theme.breakpoints.down('md')]: {
      fontSize: 14,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
    },
  },
  tab1: {
    fontFamily: "Open Sans",
    fontWeight: 700,
    fontSize: 14, // Adjusted font size
    color: "black",
    lineHeight: "150%",
    padding: "10px 8px", // Adjusted padding
    width: 120, // Adjusted width
    textTransform: "capitalize",
    [theme.breakpoints.down('xl')]: {
      padding: "6px",
    },
    [theme.breakpoints.down('lg')]: {
      minWidth: "100px",
      fontSize: 14,
      margin: 0,
    },
    [theme.breakpoints.down('md')]: {
      fontSize: 12,
      minWidth: "80px",
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 10,
    },
    "&:focus": {
      color: "black"
    }
  },
  disabledText: {
    color: "#bdbdbd",
    fontFamily: "Open Sans",
    fontWeight: 700,
    fontSize: 14,
    lineHeight: "150%",
    padding: "10px 8px", // Adjusted padding
    textTransform: "capitalize",
    width: 120, // Adjusted width
    opacity: 0.5,
    [theme.breakpoints.down('xl')]: {
      padding: "6px",
    },
    [theme.breakpoints.down('lg')]: {
      minWidth: "100px",
      fontSize: 14,
      margin: 0,
    },
    [theme.breakpoints.down('md')]: {
      fontSize: 12,
      minWidth: "80px",
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 10,
    },
  },
  root: {
    "& > *": {
      margin: theme.spacing(1),
    },
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

function allyProps(index, tabType) {
  return {
    id: `${tabType}-tab-${index}`,
    "aria-controls": `${tabType}-tabpanel-${index}`,
  };
}
TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

const Dashboard = ({ isVerified, currentUser }) => {
  const appState = useContext(GlobalContext);
  const { reloadThisData, setWhatToReload, navTeamCredits, setNavTeamCredits } =
    appState;
  const [teamsUserCreated, setCreatedTeams] = useState([]);
  const [teamsUserMemberOf, setTeams] = useState([]);
  const [pastedTeams, setPastTeams] = useState([]);
  const [subTeams, setSubTeams] = useState([]);

  const [teamsFound, setTeamsFound] = useState(true);
  const [showCreatedTeams, setShowCreatedTeams] = useState(false);
  const [showMemberTeams, setShowMemberTeams] = useState(false);
  const [showPastTeams, setShowPastTeams] = useState(false);
  const [userInvites, setInviteData] = useState([]);
  const [loadingInvites, setLoadingStateInvites] = useState(true);
  const [hasReceivedInvites, setHasReceivedInvites] = useState(false);
  const [teamApplications, setTeamApplications] = useState();
  const [loadingApplication, setLoadingStateApplications] = useState(true);
  const [hasSentApps, setHasSentApps] = useState(false);
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabType, setTabType] = useState("scrollable-auto");
  const [tabindicatorstyles, setTabIndicatorStyles] = useState({});
  const [notificationStyles, setNotificationStyles] = useState("error");

  const screenSize = useScreenSize();

  // data manipulation
  const handleTeamData = (data) => {
    // Filter out subteams and regular teams
    const filteredCreatedTeams = data[0].filter(team => team.Team.type !== 'simple sub-team');
    const filteredMemberTeams = data[1].filter(team => team.Team.type !== 'simple sub-team');
    const mySubTeams = data[0].filter(team => team.Team.type === 'simple sub-team');
    const joinSubTeams = data[1].filter(team => team.Team.type === 'simple sub-team');
    const allSubTeams = mySubTeams.concat(joinSubTeams);

    // Set state for teams
    setCreatedTeams(filteredCreatedTeams);
    setTeams(filteredMemberTeams);
    setPastTeams(data[2]);
    // console.log("Filtered Past Teams:", pastTeams); // <-- Log past teams data

    // Set state for subteams
    setSubTeams(allSubTeams);

    // Update visibility states
    setShowCreatedTeams(filteredCreatedTeams.length > 0);
    setShowMemberTeams(filteredMemberTeams.length > 0);
    setShowPastTeams(data[2].length > 0);
  };

  const fetchInviteData = async () => {
    try {
      const res = await api.get("/api/v1/invites/all");
      // Set state
      setInviteData(res.data);
      // Toggle loading state
      setLoadingStateInvites(false);
    } catch (err) {
      if (err.message === "Request failed with status code 401") {
        console.error(err);
      } else {
        throw err;
      }
    }
  };

  const fetchApplicationData = async () => {
    try {
      const res = await api.get("/api/v1/invites/applications");
      // Set state
      setTeamApplications(res.data);
      // Toggle loadingApplication state
      setLoadingStateApplications(false);
    } catch (err) {
      if (err.message === "Request failed with status code 401") {
        console.error(err);
      } else {
        throw err;
      }
    }
  };

  const fetchTeamCredits = async () => {
    try {
      // Fetch credits for user
      const res = await api.get("api/v1/teams/credits");
      // Set teams credit for user
      setNavTeamCredits(res.data.tokenBalance);
    } catch (err) {
      if (err.message === "Request failed with status code 401") {
        console.error(err);
      } else {
        throw err;
      }
    }
  };

  const fetchTeams = () => {
    // Fetch data for user's teams
    api
      .get("/api/v1/teams/teams")
      .then((res) => {
        handleTeamData(res.data);
      })
      .catch((err) => {
        if (err.message === "Request failed with status code 401") {
          console.error(err);
        } else {
          throw err;
        }
      });
  };

  useEffect(() => {
    // On mount
    fetchTeamCredits();
    fetchTeams();
    fetchInviteData();
    fetchApplicationData();
  }, []);

  // On update
  useEffect(() => {
    if (loadingApplication === false) {
      // Has sent team application
      if (teamApplications.length > 0) {
        setHasSentApps(true);
      } else {
        setHasSentApps(false);
      }
    }
    if (loadingInvites === false) {
      // Received invites
      if (userInvites.length > 0) {
        setHasReceivedInvites(true);
      } else {
        setHasReceivedInvites(false);
      }
    }
    if (reloadThisData === "invite data") {
      // Fetch data
      fetchInviteData();
      // Reset context to default
      setWhatToReload("");
    }
    // Check team data
    if (reloadThisData === "teams") {
      // Fetch team data
      fetchTeams();
      // Then set context value back to default.
      setWhatToReload("");
    }
  }, [
    reloadThisData,
    setWhatToReload,
    userInvites,
    loadingInvites,
    teamApplications,
    loadingApplication,
  ]);

  //get screen size
  useEffect(() => {
    if (screenSize === "smallScreen") {
      setTabsOrientation("vertical");
      setTabType("vertical");
      setTabIndicatorStyles({});
    } else {
      setTabsOrientation("horizontal");
      setNotificationStyles("error");
      setTabType("scrollable-auto");
      setTabIndicatorStyles({
        style: {
          height: "4px",
          width: "38px",
          marginLeft: "7%",
        },
      });
    }
  }, [screenSize]);

  // styles
  const classes = useStyles();
  // Custom hook to check user on this page
  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <>
      {(screenSize === "smallScreen" || screenSize === "mediumScreen") && (
        <TeamDashboardMobile
          myTeams={teamsUserCreated}
          myTeamCredits={navTeamCredits}
          joinTeams={teamsUserMemberOf}
          subTeams={subTeams}
          pastedTeams={pastedTeams} // Pass past teams here
          myInvites={userInvites}
          hasReceivedInvites={hasReceivedInvites}
          fetchInviteData={fetchInviteData}
          loadingApplication={loadingApplication}
          hasSentApps={hasSentApps}
          teamApplications={teamApplications}
          userInvites={userInvites}
          showMemberTeams={showMemberTeams}
          showPastTeams={showPastTeams}
          isVerified={isVerified}
        />
      )}
      {screenSize === "largeScreen" && (
        <div className={classes.containerWrapper}>
          <AppBar position="static" color="inherit" elevation={0}>
            <Tabs
              TabIndicatorProps={{
                tabindicatorstyles,
              }}
              className={classes.tabs}
              value={value}
              indicatorColor="primary"
              onChange={handleChange}
              aria-label="your teams"
              variant="scrollable"
              scrollButtons="auto"
              orientation={tabsOrientation}
            >
              <Tab
                label="Managed"
                className={classes.tab1}
                disableRipple
                {...allyProps(0, tabType)}
              />
              {showMemberTeams ? (
                <Tab
                  label="Joined"
                  className={classes.tab1}
                  disableRipple
                  {...allyProps(1, tabType)}
                  disabled={!showMemberTeams}
                />
              ) : (
                <Tab
                  label="Joined"
                  className={classes.disabledText}
                  disableRipple
                  {...allyProps(1, tabType)}
                  disabled={!showMemberTeams}
                />
              )}
              {hasReceivedInvites ? (
                <Tab
                  label={
                    <Badge
                      color="error"
                      badgeContent={userInvites.length}
                      max={99}
                      variant="standard"
                    >
                      Invitations
                    </Badge>
                  }
                  className={classes.tab1}
                  disableRipple
                  {...allyProps(2, tabType)}
                  disabled={!hasReceivedInvites}
                />
              ) : (
                <Tab
                  label="Invitations"
                  className={classes.disabledText}
                  disableRipple
                  {...allyProps(2, tabType)}
                  disabled={!hasReceivedInvites}
                />
              )}
              {hasSentApps ? (
                <Tab
                  label="Applications"
                  className={classes.tab1}
                  disableRipple
                  {...allyProps(3, tabType)}
                  disabled={!hasSentApps}
                />
              ) : (
                <Tab
                  label="Applications"
                  className={classes.disabledText}
                  disableRipple
                  {...allyProps(3, tabType)}
                  disabled={!hasSentApps}
                />
              )}
              {showPastTeams ? (
                <Tab
                  label="Past Teams"
                  className={classes.tab1}
                  disableRipple
                  {...allyProps(4, tabType)}
                  disabled={!showPastTeams}
                />
              ) : (
                <Tab
                  label="Past Teams"
                  className={classes.disabledText}
                  disableRipple
                  {...allyProps(4, tabType)}
                  disabled={!showPastTeams}
                />
              )}
              {subTeams.length > 0 ? (
                <Tab
                  label="Sub Teams"
                  className={classes.tab1}
                  disableRipple
                  {...allyProps(5, tabType)}
                />
              ) : (
                <Tab
                  label="Sub Teams"
                  className={classes.disabledText}
                  disableRipple
                  {...allyProps(5, tabType)}
                  disabled
                />
              )}
            </Tabs>
          </AppBar>
          <hr className="hr--dashboard_page_break_profile" />
          <div className={classes.dashboardPanels}>
            <TabPanel value={value} index={0}>
              <MyTeams
                myTeams={teamsUserCreated}
                myTeamCredits={navTeamCredits}
                isVerified={isVerified}
                userData={currentUser}
              />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <MyTeams myTeams={teamsUserMemberOf} myTeamCredits={null} />
            </TabPanel>
            <TabPanel value={value} index={2}>
              <InvitesReceived
                myInvites={userInvites}
                hasReceivedInvites={hasReceivedInvites}
                fetchInviteData={fetchInviteData}
              />
            </TabPanel>
            <TabPanel value={value} index={3}>
              <TeamApplicationsSent
                loadingApplication={loadingApplication}
                hasSentApps={hasSentApps}
                teamApplications={teamApplications}
              />
            </TabPanel>
            <TabPanel value={value} index={4}>
              <MyTeams myTeams={pastedTeams} myTeamCredits={null} />
            </TabPanel>
            <TabPanel value={value} index={5}>
              <MyTeams myTeams={subTeams} myTeamCredits={null} isSubTeam={true} />
            </TabPanel>
          </div>
        </div>
      )}
    </>
  );
};

Dashboard.propTypes = {
  currentUser: PropTypes.object,
  isVerified: PropTypes.bool,
};

export default Dashboard;
